"use client";
import { useAuth } from "@/contexts/AuthContext";
import { apiService, PostData } from "@/services/api";
import React, { useState, useRef, useEffect, useCallback } from "react";
import { useDebounce } from "@/hooks/useDebounce";
import Image from "next/image";

export interface PostSearchProps {
  onSelect?: (post: PostData) => void;
  onSearchResults?: (results: PostData[]) => void;
  onSearchStart?: () => void;
  setSearchResults?: (results: PostData[]) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  limit?: number;
  offset?: number;
  userID?: string;
}

const PostSearch: React.FC<PostSearchProps> = ({
  onSelect,
  onSearchResults,
  onSearchStart,
  setSearchResults,
  placeholder = "Search posts...",
  className = "space-mono-text w-full h-[48px] px-4 rounded-md focus:outline-none focus:ring-2 focus:ring-[#6c2bff]/35 focus:border-[#6c2bff] border-t-[1px] border-r-[4px] border-b-[4px] border-l-[1px] border-[#151515] rounded-[12px] text-gray-900",
  disabled = false,
  limit = 20,
  offset = 0,
  userID,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  // const [searchResults, setSearchResults] = useState<PostData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const searchRef = useRef<HTMLDivElement>(null);
  const { token, user } = useAuth();
  
  // Use our custom debounce hook
  const debouncedQuery = useDebounce(searchQuery, 400);

  // Search posts when debounced query changes
  useEffect(() => {
    const searchPosts = async () => {
      if (!debouncedQuery.trim()) {
        setSearchResults([]);
        return;
      }

      setIsLoading(true);
      setError(null);
      onSearchStart?.();

      try {
        const searchParams: any = {
          limit,
          offset,
        };

        // Add userID if provided
        if (userID) {
          searchParams.userID = userID;
        }

        const response = await apiService.searchPosts(debouncedQuery, searchParams, token || undefined);
        
        if (response.statusCode === 200 && response.data && response.data.posts) {
          const results = Array.isArray(response.data.posts) ? response.data.posts : [];
          setSearchResults(results);
          onSearchResults?.(results);
        } else {
          setError('Failed to search posts');
          setSearchResults([]);
          onSearchResults?.([]);
        }
      } catch (err) {
        console.error('Search error:', err);
        setError('Failed to search posts');
        setSearchResults([]);
        onSearchResults?.([]);
      } finally {
        setIsLoading(false);
      }
    };

    searchPosts();
  }, [debouncedQuery, limit, offset, userID, token]);

  // Handle click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsFocused(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Handle post selection
  const handlePostSelect = useCallback((post: PostData) => {
    if (onSelect) {
      onSelect(post);
    }
    setSearchQuery("");
    setIsFocused(false);
  }, [onSelect]);

  // Format post content for display
  const formatPostContent = (content: string, maxLength: number = 100) => {
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength) + "...";
  };

  // Format date
  const formatDate = (timestamp: { _seconds: number; _nanoseconds: number }) => {
    const date = new Date(timestamp._seconds * 1000 + timestamp._nanoseconds / 1000000);
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  return (
    <div
      className="flex flex-col gap-4 w-full max-w-[620px] relative"
      ref={searchRef}
    >
      {/* Search Input */}
      <input
        type="text"
        placeholder={placeholder}
        value={searchQuery}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setTimeout(() => setIsFocused(false), 150)} // delay so click works
        onChange={(e) => setSearchQuery(e.target.value)}
        className={className}
        disabled={disabled}
      />
    </div>
  );
};

export default PostSearch;
